import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Form, Input, Radio, Space, Tooltip, } from 'antd';
import { txt } from 'libs/i18n';
import dayjs from 'dayjs';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import styles from './style.module.less';
export const getTimeInSeconds = (time) => {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    const totalSeconds = dayjs.duration({
        hours,
        minutes,
        seconds,
    }).asSeconds();
    return Number(totalSeconds.toFixed(3));
};
export const formatTimeToISOString = (seconds) => {
    // NOTE: rounding is necessary, because oherwise the formatted time
    // could be one millisecond lower because of javascript arithmetic error.
    const date = new Date(Math.round(seconds * 1000));
    return date.toISOString().slice(11, 23);
};
const validateForm = async (begin, end, durationInSeconds) => {
    const regex = /^\d{2}:\d{2}:\d{2}\.\d{3}$/;
    if (!regex.test(begin) || !regex.test(end)) {
        return Promise.reject(new Error(txt('timeRangeFormat')));
    }
    const beginInSeconds = getTimeInSeconds(begin);
    const endInSeconds = getTimeInSeconds(end);
    if (beginInSeconds >= endInSeconds) {
        return Promise.reject(new Error(txt('beginningBeforeEnd')));
    }
    if (beginInSeconds > durationInSeconds
        || endInSeconds > durationInSeconds) {
        return Promise.reject(new Error(txt('notGreaterThanRecording')));
    }
    return Promise.resolve();
};
const TimerangeSelectionForm = ({ projectDuration, preselectedRange, form, title, infoTooltipText, submitLabel, loading = false, buttonDisabled, hideSubmitButton = false, }) => {
    const wasRangePreselected = preselectedRange !== null
        && preselectedRange.end !== Infinity;
    const [selectedOption, setSelectedOption] = useState(wasRangePreselected ? 'part' : 'full');
    const fullLength = {
        begin: '00:00:00.000',
        end: formatTimeToISOString(projectDuration),
    };
    const handleOptionChange = (e) => {
        setSelectedOption(e.target.value);
        if (e.target.value === 'full') {
            form.setFieldsValue({ begin: fullLength.begin, end: fullLength.end });
        }
    };
    useEffect(() => {
        const selectedRangeEnd = wasRangePreselected ? preselectedRange.end : projectDuration;
        form.setFieldsValue({
            begin: preselectedRange === null
                ? '00:00:00.000'
                : formatTimeToISOString(preselectedRange.begin),
            end: preselectedRange === null
                ? formatTimeToISOString(projectDuration)
                : formatTimeToISOString(selectedRangeEnd),
        });
    }, [preselectedRange]);
    const options = [
        {
            label: _jsx("div", { children: txt('wholeTranscription') }),
            value: 'full',
        }, {
            label: (_jsxs("div", { children: [_jsxs(Space, { children: [_jsx("span", { children: txt('partOfTranscription') }), infoTooltipText !== undefined && (_jsx(Tooltip, { title: infoTooltipText, children: _jsx(InfoCircleOutlined, {}) }))] }), _jsxs("div", { className: styles.inputs, children: [_jsx(Form.Item, { name: "begin", rules: [
                                    {
                                        required: true,
                                        whitespace: true,
                                        message: txt('fillInSubtitlesStart'),
                                    },
                                    ({ getFieldValue }) => ({
                                        async validator(_, begin) {
                                            const end = getFieldValue('end');
                                            return validateForm(begin, end, projectDuration);
                                        },
                                    }),
                                ], children: _jsx(Input, { placeholder: fullLength.begin, maxLength: 12, disabled: selectedOption === 'full', allowClear: true }) }), _jsx(Form.Item, { name: "end", rules: [
                                    {
                                        required: true,
                                        whitespace: true,
                                        message: txt('fillInSubtitlesEnd'),
                                    },
                                    ({ getFieldValue }) => ({
                                        async validator(_, end) {
                                            const begin = getFieldValue('begin');
                                            return validateForm(begin, end, projectDuration);
                                        },
                                    }),
                                ], children: _jsx(Input, { placeholder: fullLength.end, disabled: selectedOption === 'full', maxLength: 12, allowClear: true }) })] })] })),
            value: 'part',
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsx("h4", { className: styles.heading, children: title }), _jsx("p", { children: txt('rangeTip') }), _jsx(Radio.Group, { className: styles.radioGroup, value: selectedOption, onChange: handleOptionChange, options: options }), !hideSubmitButton && (_jsx("div", { className: styles.submit, children: _jsx(Button, { type: "primary", htmlType: "submit", disabled: loading || buttonDisabled, loading: loading, children: submitLabel }) }))] }));
};
export default TimerangeSelectionForm;
